import { ESymbolCategory, ISymbol, ISymbolDetail, marketAPIController } from '@aqx-core/common/api/market';
import { optionAPIController } from '@aqx-core/common/api/option';
import { EWatchlistCategory } from '@aqx-core/common/api/watchlist';
import { tradeAnalysisViewController } from '@aqx-core/common/services/trade/analysis/controller';
import { TMappedOptionList, optionService } from '@aqx-core/common/services/trade/analysis/option/service';
import { tradeWatchlistService } from '@aqx-core/common/services/trade/watchlist';
import { tradeWatchlistViewController } from '@aqx-core/common/services/trade/watchlist/controller';
import {
  IOptionTopicLiveData,
  ISymbolTopicLivePrices,
  OPTION_SUB_KEY_UIOPTION,
  SYM_SUB_KEY_UITRADE_UNDERLYING_HEADER,
  optionTopicSubscription,
  socketService,
  symbolTopicSubscription
} from '@aqx-core/common/socket';
import { ESortType } from '@aqx-core/common/utils/sorting/types';

import { EUIBuySellType } from '../../form/types';
import { ISymbolDetailData } from '../types';
import { EUIOptionSide } from './types';

class OptionViewController {
  private mappedOptionList: TMappedOptionList = {};
  private selectedOptionExpirationIds: string[] = [];
  private underlyingSymbolDetail: Partial<ISymbolDetail> = {};
  private underlyingSymbolId = '';
  private symbolToSubscribeSet = new Set<string>();

  public init = (underlyingSymbolId: string) => {
    optionService.setLoading(true);
    this.subscribeOptionTopic(underlyingSymbolId);
    this.getOptionList(underlyingSymbolId);
  };

  public reset = () => {
    this.unsubscribeOptionTopic();
    this.updateSelectedExpiration(undefined);
    optionService.setOptionExpirationIds([]);
  };

  /** To fetch the latest strike prices for when user toggle the accordion */
  public getOptionListByExpiration = async (underlyingSymbol?: string, expiration?: string) => {
    if (!underlyingSymbol || !expiration) {
      return;
    }
    await optionAPIController.getOptionList(
      { symbol: underlyingSymbol, expiration },
      {
        onSuccess: (optionList) => {
          if (optionList) {
            const filterdOptionList = optionList.filter(
              ({ optionPriceList }) => optionPriceList && optionPriceList.length > 0
            );
            filterdOptionList.forEach((option) => {
              this.mappedOptionList[expiration] = option;
            });
            optionService.setMappedOptionList(this.mappedOptionList);
          }
          optionService.setLoading(false);
        },
        onError: () => {
          optionService.setLoading(false);
        }
      }
    );
  };

  public getOptionList = async (underlyingSymbol?: string) => {
    if (!underlyingSymbol) {
      optionService.setLoading(false);
      return;
    }

    const symbolDetail = tradeAnalysisViewController.getSymbolDetail();

    await optionAPIController.getOptionList(
      { symbol: underlyingSymbol },
      {
        onSuccess: (optionList) => {
          if (optionList) {
            const filterdOptionList = optionList.filter(
              ({ optionPriceList }) => optionPriceList && optionPriceList.length > 0
            );
            const tempOptionList: TMappedOptionList = {};

            filterdOptionList.forEach((option) => {
              tempOptionList[option.expiration] = option;
            });
            this.mappedOptionList = tempOptionList;
            optionService.setMappedOptionList(this.mappedOptionList);
            optionService.setOptionExpirationIds(filterdOptionList.map((option) => option.expiration));

            /** If the selected symbol is not option, set the first expiration date */
            if (symbolDetail?.type !== ESymbolCategory.OPTION) {
              this.getFirstExpirationDate();
            }
          }
          optionService.setLoading(false);
        },
        onError: () => {
          optionService.setLoading(false);
        }
      }
    );
  };

  public onSelectOption = (symbol: string, buySellType: EUIBuySellType) => {
    // If the symbol is not in the display list , change the tab to all
    if (!tradeWatchlistService.context.displayWatchlistSymbolIds.includes(symbol)) {
      tradeWatchlistViewController.onWatchlistFilterChanged(EWatchlistCategory.ALL);
      tradeWatchlistViewController.onAllSymbolCategoryChanged(ESymbolCategory.OPTION);
    }
    tradeAnalysisViewController.setSymbolDetail({
      symbolId: symbol,
      symbolType: ESymbolCategory.OPTION,
      buySellType
    });
  };

  public onSelectOptionSortType = (sortType: ESortType) => {
    optionService.setOptionSortType(sortType);
  };

  public onSelectOptionSide = (optionSide: EUIOptionSide) => {
    optionService.setOptionSide(optionSide);
  };

  // Get latest option list base on the expiration
  public onSelectOptionExpirationId = async (expirationId: string | undefined) => {
    if (!expirationId) {
      return;
    }
    this.getOptionListByExpiration(this.underlyingSymbolId, expirationId);
    this.updateSelectedExpiration(this.selectedOptionExpirationIds.includes(expirationId) ? undefined : expirationId);
  };

  // If no selectedExpiration is present, set the first expiration date
  public getFirstExpirationDate = async () => {
    const optionList = Object.values(this.mappedOptionList);
    if (optionList.length > 0 && this.selectedOptionExpirationIds.length === 0) {
      this.updateSelectedExpiration(
        this.selectedOptionExpirationIds.includes(optionList[0].expiration) ? undefined : optionList[0].expiration
      );
    }
  };

  // When user select the option from the symbol watchlist , fetch the relevant option list
  // And expand the specific expiration and highlight the symbol */
  public getOptionExpirationIdsFromSymbolDetail = () => {
    const symbolDetail = tradeAnalysisViewController.getSymbolDetail();
    const expiration = symbolDetail?.expiration;
    if (expiration) {
      if (this.underlyingSymbolId !== symbolDetail?.underlyingSymbols?.[0]) {
        this.getOptionList(this.underlyingSymbolId);
      }
      this.updateSelectedExpiration(expiration);
    }
  };

  /** Underlying symbol */
  public setUnderlyingSymbol = async (
    symbolId: string | undefined,
    symbolInfo: { symbolDetail?: ISymbolDetail; symbol?: ISymbol },
    shouldFetch = true
  ) => {
    if (!symbolId || (this.underlyingSymbolId === symbolId && !symbolInfo.symbolDetail)) {
      return;
    }

    this.unsubscribeSymbolTopic();
    optionService.setUnderlyingSymbolId(symbolId);
    // Unsuscribe the previous option topic
    this.unsubscribeOptionTopic();
    this.underlyingSymbolId = symbolId;
    this.subscribeOptionTopic(symbolId);

    const { symbolDetail, symbol } = symbolInfo;
    if (symbol && !symbolDetail) {
      //* Temporary set partial symbol detail, handle full symbol detail when fetch complete or set with symbolDetail
      optionService.setUnderlyingSymbolDetail(symbol);
    }

    if (!symbolDetail) {
      if (!shouldFetch) {
        return;
      }

      const shouldLoading = !symbol;
      optionService.setUnderlyingSymbolLoading(shouldLoading);
      try {
        await marketAPIController.getSymbolDetail(
          { symbol: symbolId },
          {
            onSuccess: (symbolDetail) => {
              if (symbolDetail && this.underlyingSymbolId === symbolDetail.symbol) {
                this.underlyingSymbolDetail = symbolDetail;
                this.subscribeSymbolTopic();

                optionService.setUnderlyingSymbolDetail(symbolDetail);
                optionService.setUnderlyingSymbolLoading(false);
              }
            },
            onError: () => {
              optionService.setUnderlyingSymbolLoading(false);
            }
          }
        );
      } finally {
        optionService.setUnderlyingSymbolLoading(false);
      }
    } else {
      this.underlyingSymbolDetail = symbolDetail;
      this.subscribeSymbolTopic();

      optionService.setUnderlyingSymbolDetail(symbolDetail);
    }
  };

  // Socket Reconnection
  public initSocketReconnectionListener = () => {
    socketService.addReconnectListener(this.onReconnect);
  };

  public removeSocketReconnectionListener = () => {
    socketService.removeReconnectListener(this.onReconnect);
  };

  /** Private functions */
  private updateOptionItem = (id: string, newItem: IOptionTopicLiveData): void => {
    Object.values(this.mappedOptionList).forEach((option) => {
      option?.optionPriceList?.forEach((optionPrice, index) => {
        if (optionPrice?.call?.symbol === id) {
          option.optionPriceList[index].call = { ...optionPrice.call, ...newItem };
        } else if (optionPrice?.put?.symbol === id) {
          option.optionPriceList[index].put = { ...optionPrice.put, ...newItem };
        }
      });
    });
    optionService.setMappedOptionList(this.mappedOptionList);
  };

  private updateOptionData = (id: string, updatedData: IOptionTopicLiveData) => {
    this.updateOptionItem(id, updatedData);
  };

  private onReconnect = () => {
    this.refetchUnderlyingSymbolDetail();
    this.refetchOptionList();
  };

  // Sockets
  private subscribeOptionTopic = (underlyingSymbolId: string) => {
    if (this.underlyingSymbolId !== underlyingSymbolId) {
      return;
    }
    if (!this.symbolToSubscribeSet.has(underlyingSymbolId)) {
      optionTopicSubscription.subscribe(underlyingSymbolId, OPTION_SUB_KEY_UIOPTION, this.updateOptionData);
      this.symbolToSubscribeSet.add(underlyingSymbolId);
    }
  };

  private unsubscribeOptionTopic = () => {
    for (const underlyingSymbolId of this.symbolToSubscribeSet) {
      optionTopicSubscription.unsubscribe(underlyingSymbolId, OPTION_SUB_KEY_UIOPTION, this.updateOptionData);
      this.symbolToSubscribeSet.delete(underlyingSymbolId);
    }
  };

  private subscribeSymbolTopic = () => {
    const { symbol } = this.underlyingSymbolDetail;
    if (symbol) {
      symbolTopicSubscription.subscribe(symbol, SYM_SUB_KEY_UITRADE_UNDERLYING_HEADER, this.updateSymbolPrice);
    }
  };

  private unsubscribeSymbolTopic = () => {
    const { symbol } = this.underlyingSymbolDetail;
    if (symbol) {
      symbolTopicSubscription.unsubscribe(symbol, SYM_SUB_KEY_UITRADE_UNDERLYING_HEADER, this.updateSymbolPrice);
    }
  };

  private updateSymbolPrice = (id: string, livePrices: ISymbolTopicLivePrices) => {
    if (this?.underlyingSymbolDetail.symbol === id) {
      const updatedSymbolDetail: Partial<ISymbolDetailData> = {
        ...this.underlyingSymbolDetail,
        low: livePrices.liveLow,
        high: livePrices.liveHigh,
        ask: livePrices.liveBuy,
        bid: livePrices.liveSell,
        lastPrice: livePrices.liveLastPrice,
        spread: livePrices.liveSpread
      };
      this.underlyingSymbolDetail = updatedSymbolDetail;
      optionService.setUnderlyingSymbolDetail(updatedSymbolDetail);
    }
  };

  private refetchUnderlyingSymbolDetail = async () => {
    const { symbol } = this.underlyingSymbolDetail;
    if (symbol) {
      await marketAPIController.getSymbolDetail(
        { symbol },
        {
          onSuccess: (symbolDetail) => {
            if (symbolDetail) {
              this.underlyingSymbolDetail = symbolDetail;
              optionService.setUnderlyingSymbolDetail(symbolDetail);
            }
          },
          onError: () => {
            optionService.setUnderlyingSymbolLoading(false);
          }
        }
      );
    }
  };

  private updateSelectedExpiration = (expiration: string | undefined) => {
    this.selectedOptionExpirationIds = expiration ? [expiration] : [];
    optionService.setSelectedOptionExpirationIds(this.selectedOptionExpirationIds);
  };

  private refetchOptionList = async () => {
    if (this.underlyingSymbolId) {
      await this.getOptionList(this.underlyingSymbolId);
    }
  };
}

export const optionViewController = new OptionViewController();
